import React, { useCallback, useEffect, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import Head from 'expo-router/head';
import { useTheme } from '../../src/theme';
import { Header } from '@components/layout';
import { useDesktopShell } from '@hooks/useDesktopShell';
import { PostComposer } from '@components/post-create';
import type { PostSubmitOutcome } from '@hooks/usePostSubmit';
import { useAuthStore } from '../../src/store/authStore';
import { useUIStore } from '../../src/store/uiStore';

/**
 * Route wrapper for the observation composer: params, the page title/Header,
 * tab-bar visibility while this screen is up, and turning a submit outcome
 * into navigation. The form itself lives in PostComposer (panel-system.md
 * Phase 3) so the desktop map's docked composer (ComposerDockPanel.web.tsx)
 * can reuse it without a route. Phone and deep links (share links, "New
 * post" below the desktop shell) still land here.
 */
export default function CreatePostScreen() {
  const { structureId, editPostId, checklistItemId, beforePostId, category } =
    useLocalSearchParams<{
      structureId?: string;
      editPostId?: string;
      checklistItemId?: string;
      beforePostId?: string;
      category?: string;
    }>();
  const { colors } = useTheme();
  const { user } = useAuthStore();
  const { hideTabBar, showTabBar } = useUIStore();
  // A pushed picker (location/checklist/before-post) renders its own back
  // arrow (PanelStack's drill-in header) that returns to this composer —
  // this page's own arrow, which would instead leave the composer and lose
  // the draft, hides while one is up so only one arrow ever shows
  // (panel-system-review-notes.md #12a).
  const [isDrilledIn, setIsDrilledIn] = useState(false);
  const isDesktopShell = useDesktopShell();

  // Direct hits to /post/create without a session still need a graceful
  // path back — the floating "+" sheet handles the typical flow by
  // silently creating an anonymous Fire Scout before navigating here, but
  // a deep-link or back-button race could land us here with no user.
  useEffect(() => {
    if (!user) router.replace('/(tabs)');
  }, [user]);

  useEffect(() => {
    hideTabBar();
    return () => showTabBar();
  }, [hideTabBar, showTabBar]);

  const screenTitle = editPostId
    ? category === 'news'
      ? 'Edit News'
      : 'Edit Observation'
    : category === 'news'
      ? 'Share News'
      : 'New Observation';

  // Every router call the inline composer used to make directly, now
  // decided here from what usePostSubmit reports instead of inside it —
  // see PostSubmitOutcome's doc comment for exactly which call this
  // replaces.
  const handleDone = useCallback((result: PostSubmitOutcome) => {
    switch (result.kind) {
      case 'updated':
      case 'newsPosted':
        router.back();
        return;
      case 'queued':
      case 'created':
        if (result.structureId) {
          router.replace(`/structure/${result.structureId}`);
        } else {
          router.back();
        }
    }
  }, []);

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Head>
        <title>{`${screenTitle} - Redi`}</title>
      </Head>
      {/* Icons-only top bar (mockup dethead) — the big light title PostComposer
          renders carries the screen, like post detail. Single submit
          affordance: the ink pill at the bottom of the form. While a picker
          drill-in is pushed (isDrilledIn above) its own back arrow is the
          only one: on desktop the whole bar goes, since nothing else is in
          it and an empty bar with its rule sat over the picker (Sept 28);
          phone keeps the bar, which carries the safe-area inset, and just
          drops its arrow. */}
      {!(isDrilledIn && isDesktopShell) && (
        <Header showBack={!isDrilledIn} showAvatar={false} backLabel="Back" />
      )}

      <PostComposer
        editPostId={editPostId}
        structureId={structureId}
        checklistItemId={checklistItemId}
        beforePostId={beforePostId}
        category={category}
        onDone={handleDone}
        onDrillInChange={setIsDrilledIn}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
});
